import React, { useState } from "react";
import logo from "../img/icon1.jpeg";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth, storage, db } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";

const Register = () => {
  const [err, setErr] = useState(false);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const displayName = e.target.displayName.value;
    const email = e.target.email.value;
    const password = e.target.password.value;
    const file = e.target.avatar.files[0];

    try {
      const res = await createUserWithEmailAndPassword(auth, email, password);

      const storageRef = ref(storage, `users/${res.user.uid}/${displayName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {},
        (error) => {
          console.error(error);
          setErr(true);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            await updateProfile(res.user, {
              displayName,
              photoURL: downloadURL,
            });

            await setDoc(doc(db, "users", res.user.uid), {
              uid: res.user.uid,
              displayName,
              email,
              photoURL: downloadURL,
            });

            await setDoc(doc(db, "userChats", res.user.uid), {});

            navigate("/");
          } catch (error) {
            console.error(error);
            setErr(true);
          }
        },
      );
    } catch (error) {
      console.error(error);
      if (error.code === "auth/email-already-in-use") {
        setErr("Email address is already in use.");
      } else {
        setErr("Something went wrong. Please try again later.");
      }
    }
  };

  return (
    <div className="register-page">
      <div className="register-bg" aria-hidden="true">
        <div className="register-bg__dots register-bg__dots--tl" />
        <div className="register-bg__dots register-bg__dots--tr" />
        <div className="register-bg__curve register-bg__curve--1" />
        <div className="register-bg__curve register-bg__curve--2" />
        <div className="register-bg__dot register-bg__dot--1" />
        <div className="register-bg__dot register-bg__dot--2" />
        <div className="register-bg__dot register-bg__dot--3" />
      </div>

      <header className="register-header">
        <div className="register-brand">
          <span className="register-brand__icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H6L10 22L14 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                fill="currentColor"
              />
              <circle cx="8" cy="10" r="1.2" fill="white" />
              <circle cx="12" cy="10" r="1.2" fill="white" />
              <circle cx="16" cy="10" r="1.2" fill="white" />
            </svg>
          </span>
          <span className="register-brand__text">Chat Cafe</span>
        </div>
      </header>

      <div className="register-layout">
        <section className="register-left">
          <span className="register-badge">✨ Welcome to Chat Cafe</span>
          <h1 className="register-headline">
            Join the conversation.{" "}
            <span className="register-headline__accent">Connect</span> with the
            world.
          </h1>
          <p className="register-subtext">
            Create your account and start chatting instantly with friends and
            communities.
          </p>

          <ul className="register-features">
            <li className="register-feature">
              <span className="register-feature__icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H6L10 22L14 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div>
                <strong>Real-time Messaging</strong>
                <p>Instant, secure and reliable conversations</p>
              </div>
            </li>
            <li className="register-feature">
              <span className="register-feature__icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L4 6V12C4 16.42 7.58 20.24 12 22C16.42 20.24 20 16.42 20 12V6L12 2Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 12L11 14L15 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div>
                <strong>Privacy</strong>
                <p>Your data stays safe and protected</p>
              </div>
            </li>
            <li className="register-feature">
              <span className="register-feature__icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="9"
                    cy="7"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <circle
                    cx="17"
                    cy="8"
                    r="2.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M3 19C3 16.24 5.69 14 9 14C12.31 14 15 16.24 15 19M15 17.5C15 15.57 16.34 14 18 14C19.66 14 21 15.57 21 17.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <div>
                <strong>Connect Everywhere</strong>
                <p>Chat from any device, anytime</p>
              </div>
            </li>
          </ul>
        </section>

        <section className="register-center">
          <div className="register-card">
            <div className="register-card__header">
              <span className="register-card__logo">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H6L10 22L14 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                    fill="currentColor"
                  />
                  <circle cx="8" cy="10" r="1.2" fill="white" />
                  <circle cx="12" cy="10" r="1.2" fill="white" />
                  <circle cx="16" cy="10" r="1.2" fill="white" />
                </svg>
              </span>
              <h2 className="register-card__title">Create your account</h2>
              <p className="register-card__subtitle">
                Sign up to get started with Chat Cafe
              </p>
            </div>

            <form className="register-form" onSubmit={handleSubmit}>
              <div className="register-field">
                <label htmlFor="displayName">Display Name</label>
                <div className="register-input">
                  <span className="register-input__icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="8"
                        r="4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M5 20C5 16.13 8.13 13 12 13C15.87 13 19 16.13 19 20"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    placeholder="Enter your display name"
                  />
                </div>
              </div>

              <div className="register-field">
                <label htmlFor="email">Email</label>
                <div className="register-input">
                  <span className="register-input__icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M3 7L12 13L21 7"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div className="register-field">
                <label htmlFor="password">Password</label>

                <div className="register-input">
                  <span className="register-input__icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="5"
                        y="11"
                        width="14"
                        height="10"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M8 11V8C8 5.79 9.79 4 12 4C14.21 4 16 5.79 16 8V11"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>

                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Create a password"
                  />

                  <span
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                    >
                      <path
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </div>

              <div className="register-field">
                <input
                  className="register-avatar-input"
                  type="file"
                  id="avatar"
                  name="avatar"
                />
                <label className="register-avatar" htmlFor="avatar">
                  <span className="register-avatar__icon">
                    <img src={logo} alt="" />
                  </span>
                  <span className="register-avatar__text">
                    <span className="avatar">Add an avatar</span>
                    <span className="register-avatar__hint">
                      JPG, PNG up to 2MB
                    </span>
                  </span>
                </label>
              </div>

              <button className="register-button" type="submit">
                <span>Sign up</span>
                <span className="register-button__arrow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L13 6M19 12L13 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>

              {err && <span className="register-error">{err}</span>}
            </form>

            <p className="register-footer">
              You do have an account?{" "}
              <span className="last">
                <Link to="/login">Login</Link>
              </span>
            </p>
          </div>
        </section>

        <aside className="register-preview" aria-hidden="true">
          <div className="register-preview__panel">
            <div className="register-preview__header">
              <span className="register-preview__brand-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H6L10 22L14 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <div>
                <strong>Chat Cafe</strong>
                <span className="register-preview__status">
                  <span className="register-preview__dot" />
                  Online
                </span>
              </div>
            </div>

            <div className="register-preview__messages">
              <div className="register-preview__msg">
                <span className="register-preview__avatar register-preview__avatar--1">
                  A
                </span>
                <div className="register-preview__msg-body">
                  <div className="register-preview__msg-top">
                    <strong>Alice Johnson</strong>
                    <span>2m</span>
                  </div>
                  <p>Hey there! 👋</p>
                </div>
              </div>

              <div className="register-preview__msg">
                <span className="register-preview__avatar register-preview__avatar--2">
                  M
                </span>
                <div className="register-preview__msg-body">
                  <div className="register-preview__msg-top">
                    <strong>Mike Chen</strong>
                    <span>5m</span>
                  </div>
                  <p>Ready to chat?</p>
                </div>
              </div>

              <div className="register-preview__msg">
                <span className="register-preview__avatar register-preview__avatar--3">
                  S
                </span>
                <div className="register-preview__msg-body">
                  <div className="register-preview__msg-top">
                    <strong>Sarah Lee</strong>
                    <span>12m</span>
                  </div>
                  <p>Welcome aboard! 🎉</p>
                </div>
              </div>
            </div>

            <div className="register-preview__welcome">
              <span className="register-preview__welcome-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 2H4C2.9 2 2 2.9 2 4V16C2 17.1 2.9 18 4 18H6L10 22L14 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <div>
                <strong>Welcome to Chat Cafe! 🎉</strong>
                <p>Start your journey now · just now</p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Register;
